import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Box, Loader2, Eye, Wrench, FileText, Settings, GitBranch, Clock, AlertTriangle, CheckCircle2, Package } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const HEALTH = { healthy: { color: "#10B981", bg: "#F0FDF4", border: "#BBF7D0", label: "Healthy" }, caution: { color: "#F59E0B", bg: "#FFFBEB", border: "#FDE68A", label: "Caution" }, warning: { color: "#D97706", bg: "#FFF7ED", border: "#FED7AA", label: "Warning" }, critical: { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", label: "Critical" } };
const EVENT_ICONS = { workorder: Wrench, maintenance: Settings, blueprint: FileText, ecr: GitBranch };

export default function DigitalTwinPage({ onLogout, userRole, isFeatureEnabled }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailMachine, setDetailMachine] = useState(null);
  const [twinData, setTwinData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [tab, setTab] = useState("overview");

  const fetchAll = useCallback(async () => {
    try { const r = await axios.get(`${API}/digital-twin/machines`, { headers: getAuthHeaders() }); setData(r.data); }
    catch { toast.error("Failed to load twins"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openTwin = async (mn) => {
    setDetailMachine(mn);
    setTab("overview");
    try {
      const [tR, tlR] = await Promise.all([
        axios.get(`${API}/digital-twin/machines/${mn}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/digital-twin/machines/${mn}/timeline`, { headers: getAuthHeaders() }),
      ]);
      setTwinData(tR.data);
      setTimeline(tlR.data.events || []);
    } catch { toast.error("Failed to load twin data"); }
  };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;

  const summary = data?.summary || {};
  const twins = data?.twins || [];

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="digital-twin-page">
        <PageHeader title="Asset Digital Twin" subtitle="Unified machine view with real-time status from all modules" testId="twin-title" />

        {/* Plant Health Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="twin-stats">
          {[["Total", summary.total, "#0F172A"], ["Healthy", summary.healthy, "#10B981"], ["Caution", summary.caution, "#F59E0B"], ["Warning", summary.warning, "#D97706"], ["Critical", summary.critical, "#DC2626"]].map(([l, v, c]) => (
            <div key={l} className="bg-white border border-[#E2E8F0] px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{l}</p><p className="text-2xl font-heading font-black mt-0.5" style={{ color: c }}>{v || 0}</p></div>
          ))}
        </div>

        {/* Plant Schematic (simplified grid) */}
        <div className="bg-white border border-[#E2E8F0] p-4" data-testid="plant-schematic">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-3">Plant Floor — Machine Status</p>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {twins.map(t => {
              const h = HEALTH[t.health] || HEALTH.healthy;
              return (
                <div key={t.id} onClick={() => openTwin(t.machine_number)} className="border-2 p-3 cursor-pointer hover:shadow-md transition-shadow text-center" style={{ borderColor: h.color, backgroundColor: h.bg }} data-testid={`twin-node-${t.machine_number}`}>
                  <Box size={20} style={{ color: h.color }} className="mx-auto" />
                  <p className="font-mono text-xs font-bold text-[#0F172A] mt-1">{t.machine_number}</p>
                  <p className="text-[9px] text-[#64748B] truncate">{t.name}</p>
                  <span className="text-[8px] font-bold uppercase mt-1 inline-block px-1.5 py-0.5" style={{ color: h.color, backgroundColor: `${h.color}15` }}>{h.label}</span>
                  <div className="flex justify-center gap-2 mt-1 text-[9px] text-[#64748B]">
                    <span>{t.stats.open_wos} WO</span>
                    <span>{t.stats.overdue_maint} OD</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Machine List */}
        <div className="bg-white border border-[#E2E8F0]" data-testid="twin-list">
          <div className="divide-y divide-[#F1F5F9]">
            {twins.map(t => {
              const h = HEALTH[t.health] || HEALTH.healthy;
              return (
                <div key={t.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA] cursor-pointer" onClick={() => openTwin(t.machine_number)} data-testid={`twin-row-${t.machine_number}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-3 h-3 shrink-0" style={{ backgroundColor: h.color }} />
                    <div>
                      <div className="flex items-center gap-2"><span className="font-mono text-xs font-bold text-[#0F172A]">{t.machine_number}</span><span className="text-[10px] px-2 py-0.5 font-bold uppercase" style={{ color: h.color, backgroundColor: h.bg, border: `1px solid ${h.border}` }}>{h.label}</span></div>
                      <p className="text-[11px] text-[#64748B]">{t.name} · {t.type}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-[10px] text-[#64748B] shrink-0">
                    <span>{t.stats.blueprints} BP</span>
                    <span className={t.stats.open_wos > 0 ? "text-[#D97706] font-bold" : ""}>{t.stats.open_wos} Open WO</span>
                    <span className={t.stats.overdue_maint > 0 ? "text-[#DC2626] font-bold" : ""}>{t.stats.overdue_maint} Overdue</span>
                    <span>{t.stats.parts} Parts</span>
                    <span>{t.stats.config_items} CI</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Twin Detail Dialog */}
      <Dialog open={!!detailMachine} onOpenChange={(o) => { if (!o) { setDetailMachine(null); setTwinData(null); } }}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-3xl max-h-[85vh] overflow-y-auto p-0" data-testid="twin-detail-dialog">
          {twinData ? (
            <>
              <div className="px-5 py-4 border-b border-[#E2E8F0]" style={{ backgroundColor: HEALTH[twinData.health]?.bg }}>
                <DialogHeader>
                  <DialogTitle className="text-[#0F172A] flex items-center gap-2">
                    <Box size={18} style={{ color: HEALTH[twinData.health]?.color }} />
                    {twinData.machine?.machine_number} — {twinData.machine?.name}
                  </DialogTitle>
                  <DialogDescription className="text-xs text-[#64748B]">Digital Twin · {twinData.machine?.type} · {twinData.machine?.location || "—"}</DialogDescription>
                </DialogHeader>
                <div className="flex gap-3 mt-3">
                  {Object.entries(twinData.stats || {}).map(([k, v]) => (
                    <div key={k} className="text-center"><p className="text-lg font-black text-[#0F172A]">{v}</p><p className="text-[8px] uppercase text-[#94A3B8]">{k.replace(/_/g, " ")}</p></div>
                  ))}
                </div>
              </div>
              <div className="px-5 py-3 border-b border-[#E2E8F0] flex gap-1">
                {["overview", "timeline"].map(t => (
                  <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-xs font-bold tracking-wider ${tab === t ? "bg-[#0F172A] text-white" : "text-[#64748B]"}`} data-testid={`twin-tab-${t}`}>{t === "overview" ? "Overview" : "Timeline"}</button>
                ))}
              </div>
              <div className="p-5 space-y-4">
                {tab === "overview" && (
                  <>
                    {/* Work Orders */}
                    {(twinData.work_orders || []).length > 0 && (
                      <Section title="Work Orders" icon={<Wrench size={12} />} count={twinData.work_orders.length}>
                        {twinData.work_orders.slice(0, 5).map(w => <MiniRow key={w.id} label={w.title} status={w.status} sub={w.priority} />)}
                      </Section>
                    )}
                    {/* Maintenance */}
                    {(twinData.maintenance || []).length > 0 && (
                      <Section title="Maintenance" icon={<Settings size={12} />} count={twinData.maintenance.length}>
                        {twinData.maintenance.slice(0, 5).map(m => <MiniRow key={m.id} label={m.title} status={m.status} sub={m.due_date} />)}
                      </Section>
                    )}
                    {/* Blueprints */}
                    {(twinData.blueprints || []).length > 0 && (
                      <Section title="Blueprints" icon={<FileText size={12} />} count={twinData.blueprints.length}>
                        {twinData.blueprints.map(b => <MiniRow key={b.id} label={b.original_filename} status={b.lifecycle_state} sub={b.blueprint_type} />)}
                      </Section>
                    )}
                    {/* Config Items */}
                    {(twinData.config_items || []).length > 0 && (
                      <Section title="Configuration Items" icon={<Settings size={12} />} count={twinData.config_items.length}>
                        {twinData.config_items.map(c => <MiniRow key={c.id} label={`${c.ci_number} ${c.name}`} status={c.status} />)}
                      </Section>
                    )}
                    {/* ECRs */}
                    {(twinData.ecrs || []).length > 0 && (
                      <Section title="Engineering Changes" icon={<GitBranch size={12} />} count={twinData.ecrs.length}>
                        {twinData.ecrs.map(e => <MiniRow key={e.id} label={`${e.ecr_number} ${e.title}`} status={e.status} />)}
                      </Section>
                    )}
                    {/* Parts */}
                    {(twinData.parts || []).length > 0 && (
                      <Section title="Parts Inventory" icon={<Package size={12} />} count={twinData.parts.length}>
                        {twinData.parts.slice(0, 8).map(p => <MiniRow key={p.id} label={p.description} sub={p.sap_number} status={`Qty: ${p.quantity ?? "—"}`} />)}
                      </Section>
                    )}
                    {/* Alterations */}
                    {(twinData.alterations || []).length > 0 && (
                      <Section title="Active Alterations" icon={<AlertTriangle size={12} />} count={twinData.alterations.length}>
                        {twinData.alterations.map(a => <MiniRow key={a.id} label={`${a.alt_number} ${a.title}`} status={a.status} />)}
                      </Section>
                    )}
                  </>
                )}
                {tab === "timeline" && (
                  <div className="space-y-2" data-testid="twin-timeline">
                    {timeline.length === 0 ? <p className="text-sm text-[#94A3B8] text-center py-6">No events recorded.</p> : (
                      timeline.map((ev, i) => {
                        const Icon = EVENT_ICONS[ev.type] || Clock;
                        return (
                          <div key={i} className="flex gap-3 text-xs" data-testid={`timeline-${i}`}>
                            <div className="w-8 h-8 bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center shrink-0"><Icon size={14} className="text-[#64748B]" /></div>
                            <div className="flex-1 min-w-0 py-1">
                              <p className="text-[#0F172A] font-medium truncate">{ev.title}</p>
                              <p className="text-[10px] text-[#94A3B8]">{ev.type} · {ev.status} · {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : "—"}</p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </>
          ) : <div className="p-12 text-center"><DialogHeader><DialogTitle className="sr-only">Loading...</DialogTitle></DialogHeader><Loader2 className="animate-spin text-[#2563EB] mx-auto" size={24} /></div>}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Section({ title, icon, count, children }) {
  return (
    <div className="border border-[#E2E8F0]">
      <div className="px-3 py-2 bg-[#FAFAFA] border-b border-[#E2E8F0] flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#64748B]">{icon} {title} ({count})</div>
      <div className="divide-y divide-[#F1F5F9]">{children}</div>
    </div>
  );
}

function MiniRow({ label, status, sub }) {
  return (
    <div className="px-3 py-2 flex items-center justify-between text-xs">
      <span className="text-[#0F172A] truncate flex-1">{label}</span>
      {sub && <span className="text-[10px] text-[#94A3B8] mx-2">{sub}</span>}
      {status && <span className="text-[10px] font-bold text-[#64748B] shrink-0">{status}</span>}
    </div>
  );
}
